/**
 * Status, visibility, run, import and document badges for the Procedures console, plus the callout
 * type visuals (icon + colour). The lookup tables are the source of truth for names and colours; the
 * fallbacks below only cover the seeded ids when the lookups have not loaded yet.
 */

import type { ReactNode } from 'react';
import {
  IconBulb,
  IconChecklist,
  IconHandStop,
  IconAlertTriangle,
  IconCircleCheck,
  IconWorld,
  IconLock,
  IconPencil,
  IconArchive,
  IconBroadcast,
  IconPlayerPlay,
  IconX,
  IconClock,
  IconScan,
  IconSparkles,
  IconAlertCircle,
  IconHourglass,
  IconCheck,
  IconPlayerSkipForward,
  IconFlag,
} from '@tabler/icons-react';
import { Badge, Tooltip } from '@/components/ui';
import {
  PROCEDURES_STATUS,
  PROCEDURES_VISIBILITY,
  PROCEDURES_CALLOUT_TYPE,
  PROCEDURES_RUN_STATUS,
  PROCEDURES_RUN_STEP_STATUS,
  PROCEDURES_IMPORT_STATUS,
  PROCEDURES_DOC_INDEX_STATUS,
} from './proceduresUtils';

interface Meta {
  label: string;
  color: string;
  icon: ReactNode;
  hint?: string;
}

const badgeStyles = { root: { textTransform: 'none' as const } };

export const STATUS_META: Record<number, Meta> = {
  [PROCEDURES_STATUS.Draft]: { label: 'Draft', color: 'gray', icon: <IconPencil size={12} />, hint: 'Not live yet. The portal never shows a draft.' },
  [PROCEDURES_STATUS.Published]: { label: 'Published', color: 'green', icon: <IconBroadcast size={12} />, hint: 'Live in the portal.' },
  [PROCEDURES_STATUS.Archived]: { label: 'Archived', color: 'dark', icon: <IconArchive size={12} />, hint: 'Retired and hidden from the portal.' },
};

export const VISIBILITY_META: Record<number, Meta> = {
  [PROCEDURES_VISIBILITY.Public]: { label: 'Public', color: 'blue', icon: <IconWorld size={12} />, hint: 'Anyone with the QR code or link can run it without signing in.' },
  [PROCEDURES_VISIBILITY.Private]: { label: 'Private', color: 'grape', icon: <IconLock size={12} />, hint: 'Only the people, roles, departments and ministries it is assigned to.' },
};

export const RUN_STATUS_META: Record<number, Meta> = {
  [PROCEDURES_RUN_STATUS.InProgress]: { label: 'In progress', color: 'blue', icon: <IconPlayerPlay size={12} /> },
  [PROCEDURES_RUN_STATUS.Completed]: { label: 'Completed', color: 'green', icon: <IconCircleCheck size={12} /> },
  [PROCEDURES_RUN_STATUS.Abandoned]: { label: 'Abandoned', color: 'gray', icon: <IconX size={12} /> },
};

export const RUN_STEP_STATUS_META: Record<number, Meta> = {
  [PROCEDURES_RUN_STEP_STATUS.Done]: { label: 'Done', color: 'green', icon: <IconCheck size={12} /> },
  [PROCEDURES_RUN_STEP_STATUS.Skipped]: { label: 'Skipped', color: 'gray', icon: <IconPlayerSkipForward size={12} /> },
  [PROCEDURES_RUN_STEP_STATUS.Problem]: { label: 'Problem', color: 'red', icon: <IconFlag size={12} /> },
};

export const IMPORT_STATUS_META: Record<number, Meta & { copy: string }> = {
  [PROCEDURES_IMPORT_STATUS.Queued]: { label: 'Queued', color: 'gray', icon: <IconClock size={12} />, copy: 'Waiting for a free slot. This usually takes a few seconds.' },
  [PROCEDURES_IMPORT_STATUS.Extracting]: { label: 'Reading', color: 'cyan', icon: <IconScan size={12} />, copy: 'Reading the text and pictures from your upload.' },
  [PROCEDURES_IMPORT_STATUS.Structuring]: { label: 'Writing steps', color: 'violet', icon: <IconSparkles size={12} />, copy: 'The AI is turning the content into clear steps, checks and warnings.' },
  [PROCEDURES_IMPORT_STATUS.Ready]: { label: 'Ready', color: 'green', icon: <IconCircleCheck size={12} />, copy: 'Your draft is ready to check and publish.' },
  [PROCEDURES_IMPORT_STATUS.Failed]: { label: 'Failed', color: 'red', icon: <IconAlertCircle size={12} />, copy: "The import couldn't finish." },
};

export const DOC_INDEX_META: Record<number, Meta> = {
  [PROCEDURES_DOC_INDEX_STATUS.Pending]: { label: 'Waiting to be read', color: 'gray', icon: <IconHourglass size={12} />, hint: 'The AI assistant will be able to answer from it shortly.' },
  [PROCEDURES_DOC_INDEX_STATUS.Indexed]: { label: 'Ready for the AI', color: 'green', icon: <IconCircleCheck size={12} />, hint: 'The AI assistant can answer from this file.' },
  [PROCEDURES_DOC_INDEX_STATUS.Failed]: { label: 'Could not be read', color: 'red', icon: <IconAlertCircle size={12} />, hint: 'Try reindexing, or upload a clearer copy.' },
};

/** Callout visuals by type id. `colorHex` from the lookup wins when present. */
export const CALLOUT_META: Record<number, { label: string; color: string; icon: (size?: number) => ReactNode }> = {
  [PROCEDURES_CALLOUT_TYPE.Tip]: { label: 'Tip', color: 'blue', icon: (s = 16) => <IconBulb size={s} /> },
  [PROCEDURES_CALLOUT_TYPE.Check]: { label: 'Check', color: 'green', icon: (s = 16) => <IconChecklist size={s} /> },
  [PROCEDURES_CALLOUT_TYPE.Careful]: { label: 'Careful', color: 'yellow', icon: (s = 16) => <IconHandStop size={s} /> },
  [PROCEDURES_CALLOUT_TYPE.Warning]: { label: 'Warning', color: 'red', icon: (s = 16) => <IconAlertTriangle size={s} /> },
  [PROCEDURES_CALLOUT_TYPE.Done]: { label: 'Done', color: 'teal', icon: (s = 16) => <IconCircleCheck size={s} /> },
};

export function calloutMeta(typeId: number) {
  return CALLOUT_META[typeId] ?? CALLOUT_META[PROCEDURES_CALLOUT_TYPE.Tip];
}

function MetaBadge({ meta, name, size = 'sm', variant = 'light', color }: { meta?: Meta; name?: string | null; size?: 'xs' | 'sm' | 'md'; variant?: 'light' | 'filled' | 'outline' | 'dot'; color?: string | null }) {
  const label = name || meta?.label || 'Unknown';
  const badge = (
    <Badge size={size} variant={variant} color={color || meta?.color || 'gray'} leftSection={meta?.icon} styles={badgeStyles}>
      {label}
    </Badge>
  );
  return meta?.hint ? (
    <Tooltip label={meta.hint} withArrow multiline w={260}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
}

export function ProcedureStatusBadge({ statusId, size }: { statusId: number; name?: string | null; size?: 'xs' | 'sm' | 'md' }) {
  return <MetaBadge meta={STATUS_META[statusId]} size={size} />;
}

export function VisibilityBadge({ visibilityId, size }: { visibilityId: number; size?: 'xs' | 'sm' | 'md' }) {
  return <MetaBadge meta={VISIBILITY_META[visibilityId]} size={size} variant="outline" />;
}

export function RunStatusBadge({ statusId, name, size }: { statusId: number; name?: string | null; size?: 'xs' | 'sm' | 'md' }) {
  return <MetaBadge meta={RUN_STATUS_META[statusId]} name={RUN_STATUS_META[statusId] ? undefined : name} size={size} />;
}

export function RunStepStatusBadge({ statusId, size }: { statusId?: number | null; size?: 'xs' | 'sm' | 'md' }) {
  if (!statusId) {
    return (
      <Badge size={size ?? 'sm'} variant="outline" color="gray" styles={badgeStyles}>
        Not reached
      </Badge>
    );
  }
  return <MetaBadge meta={RUN_STEP_STATUS_META[statusId]} size={size} />;
}

export function ImportStatusBadge({ statusId, size }: { statusId: number; size?: 'xs' | 'sm' | 'md' }) {
  return <MetaBadge meta={IMPORT_STATUS_META[statusId]} size={size} />;
}

export function DocIndexBadge({ statusId, error, size }: { statusId: number; error?: string | null; size?: 'xs' | 'sm' | 'md' }) {
  const meta = DOC_INDEX_META[statusId];
  if (error && statusId === PROCEDURES_DOC_INDEX_STATUS.Failed) {
    return (
      <Tooltip label={error} withArrow multiline w={300}>
        <Badge size={size ?? 'sm'} variant="light" color="red" leftSection={meta?.icon} styles={badgeStyles}>
          {meta?.label ?? 'Failed'}
        </Badge>
      </Tooltip>
    );
  }
  return <MetaBadge meta={meta} size={size} />;
}

export function UnpublishedBadge({ size = 'sm' }: { size?: 'xs' | 'sm' | 'md' }) {
  return (
    <Tooltip label="The draft has changes that are not live yet. Publish to make them live." withArrow multiline w={260}>
      <Badge size={size} variant="light" color="yellow" leftSection={<IconAlertTriangle size={12} />} styles={badgeStyles}>
        Unpublished changes
      </Badge>
    </Tooltip>
  );
}

export function ReviewDueBadge({ size = 'sm' }: { size?: 'xs' | 'sm' | 'md' }) {
  return (
    <Tooltip label="Past its review date. Check the steps are still right, then publish again." withArrow multiline w={260}>
      <Badge size={size} variant="light" color="orange" leftSection={<IconClock size={12} />} styles={badgeStyles}>
        Review due
      </Badge>
    </Tooltip>
  );
}
